import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { COGNITIVE_OPTIONS, MOBILITY_OPTIONS, CARE_NEEDS } from "@/lib/portalConstants";
import { Plus, Pencil, Trash2, Loader2, Users, Phone, KeyRound, UserPlus, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatPhone } from "@/lib/format";

const empty = {
  name: "", contact_name: "", contact_phone: "", contact_email: "", contact_relationship: "",
  address: "", notes: "", approx_age: "", cognitive: "", mobility: "", care_needs: [], hourly_rate: "", active: true,
};

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [familyUsers, setFamilyUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const [famOpen, setFamOpen] = useState(false);
  const [famForm, setFamForm] = useState({ name: "", email: "", password: "", client_id: "" });

  const load = async () => {
    const [c, u] = await Promise.all([api.get("/portal/clients"), api.get("/portal/users")]);
    setClients(c.data);
    setFamilyUsers(u.data);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...empty, ...c, care_needs: c.care_needs || [] }); setEditId(c.id); setOpen(true); };

  const toggleNeed = (n) => {
    const has = form.care_needs.includes(n);
    setForm({ ...form, care_needs: has ? form.care_needs.filter((x) => x !== n) : [...form.care_needs, n] });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Please enter the client's name.");
    const payload = { ...form, hourly_rate: form.hourly_rate === "" || form.hourly_rate == null ? null : parseFloat(form.hourly_rate) };
    try {
      if (editId) await api.put(`/portal/clients/${editId}`, payload);
      else await api.post("/portal/clients", payload);
      toast.success(editId ? "Client updated" : "Client added");
      setOpen(false);
      load();
    } catch {
      toast.error("Could not save client.");
    }
  };
  const remove = async (id) => {
    await api.delete(`/portal/clients/${id}`);
    toast.success("Client removed");
    load();
  };

  const openFamily = (client) => {
    setFamForm({ name: "", email: "", password: "", client_id: client.id });
    setFamOpen(true);
  };
  const saveFamily = async () => {
    if (!famForm.name || !famForm.email || famForm.password.length < 6)
      return toast.error("Name, email, and a 6+ character password are required.");
    try {
      await api.post("/portal/users", famForm);
      toast.success("Family login created");
      setFamOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const removeFamily = async (id) => {
    await api.delete(`/portal/users/${id}`);
    toast.success("Family login removed");
    load();
  };

  const familyForClient = (cid) => familyUsers.filter((u) => u.client_id === cid);

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="clients-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl text-charcoal">Clients</h1>
          <p className="text-charcoal-soft mt-1 text-sm sm:text-base">Tap a client to see their whole story — schedule, care, notes, billing.</p>
        </div>
        <button onClick={openNew} data-testid="add-client-button" className="inline-flex items-center gap-2 rounded-full bg-sage text-white px-5 py-3 text-sm font-medium hover:bg-sage-dark transition-colors">
          <Plus className="h-4 w-4" /> Add Client
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-sage" /></div>
      ) : clients.length === 0 ? (
        <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-14 text-center">
          <Users className="h-10 w-10 text-sage/50 mx-auto mb-4" />
          <p className="text-charcoal-soft">No clients yet. Add your first client to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {clients.map((c) => (
            <div
              key={c.id}
              data-testid={`client-card-${c.id}`}
              onClick={() => navigate(`/portal/clients/${c.id}`)}
              className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-6 cursor-pointer hover:-translate-y-0.5 hover:shadow-lg transition-all group"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-serif text-2xl text-charcoal inline-flex items-center gap-2">
                    {c.name}
                    <ChevronRight className="h-4 w-4 text-charcoal/25 group-hover:text-sage group-hover:translate-x-0.5 transition-all" />
                  </h3>
                  {c.contact_name && (
                    <p className="text-sm text-charcoal-soft mt-1">
                      {c.contact_name}{c.contact_relationship && ` (${c.contact_relationship})`}
                    </p>
                  )}
                </div>
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`} className="p-2 text-charcoal/40 hover:text-charcoal"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(c.id)} data-testid={`delete-client-${c.id}`} className="p-2 text-charcoal/40 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>

              {(c.approx_age || c.cognitive || c.mobility || c.hourly_rate > 0) && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {c.approx_age && <span className="text-[11px] px-2 py-0.5 rounded-full bg-sage/15 text-sage-700 font-medium">Age ~{c.approx_age}</span>}
                  {c.hourly_rate > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">${c.hourly_rate}/hr</span>}
                  {c.cognitive && <span className="text-[11px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">{c.cognitive}</span>}
                  {c.mobility && <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{c.mobility}</span>}
                </div>
              )}
              {c.care_needs?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {c.care_needs.slice(0, 5).map((n) => (
                    <span key={n} className="text-[11px] px-2 py-0.5 rounded-full bg-terracotta/10 text-terracotta">{n}</span>
                  ))}
                  {c.care_needs.length > 5 && <span className="text-[11px] text-charcoal-soft">+{c.care_needs.length - 5} more</span>}
                </div>
              )}

              {c.contact_phone && (
                <p className="flex items-center gap-2 text-sm text-charcoal-soft mt-3"><Phone className="h-4 w-4 text-sage" /> {c.contact_phone}</p>
              )}
              {c.address && <p className="text-sm text-charcoal-soft mt-1">{c.address}</p>}
              {c.notes && <p className="text-sm text-charcoal-soft mt-3 italic border-l-2 border-sage/30 pl-3 line-clamp-2">{c.notes}</p>}

              <div className="mt-5 pt-4 border-t border-charcoal/5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-charcoal-soft uppercase tracking-wide flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" /> Family Logins</p>
                  <button onClick={() => openFamily(c)} data-testid={`add-family-${c.id}`} className="text-xs text-sage-700 hover:underline flex items-center gap-1"><UserPlus className="h-3.5 w-3.5" /> Add</button>
                </div>
                {familyForClient(c.id).length === 0 ? (
                  <p className="text-xs text-charcoal/40">No family logins yet.</p>
                ) : (
                  <ul className="space-y-1">
                    {familyForClient(c.id).map((u) => (
                      <li key={u.id} className="flex items-center justify-between text-sm">
                        <span className="text-charcoal">{u.name} <span className="text-charcoal-soft">· {u.email}</span></span>
                        <button onClick={() => removeFamily(u.id)} className="text-charcoal/40 hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Client dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-sand max-w-lg max-h-[90vh] overflow-y-auto" data-testid="client-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit Client" : "Add Client"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Client name</Label>
              <Input data-testid="client-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-white h-11" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <Label>Approx. age</Label>
                <Input data-testid="client-age" value={form.approx_age} onChange={(e) => setForm({ ...form, approx_age: e.target.value })} placeholder="78" className="mt-1.5 bg-white h-11" />
              </div>
              <div>
                <Label>Hourly rate ($)</Label>
                <Input type="number" min="0" step="0.5" data-testid="client-rate" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} placeholder="32" className="mt-1.5 bg-white h-11" />
              </div>
              <div>
                <Label>Cognitive awareness</Label>
                <Select value={form.cognitive || "unset"} onValueChange={(v) => setForm({ ...form, cognitive: v === "unset" ? "" : v })}>
                  <SelectTrigger data-testid="client-cognitive" className="mt-1.5 bg-white h-11"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unset">— Not set —</SelectItem>
                    {COGNITIVE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Mobility</Label>
                <Select value={form.mobility || "unset"} onValueChange={(v) => setForm({ ...form, mobility: v === "unset" ? "" : v })}>
                  <SelectTrigger data-testid="client-mobility" className="mt-1.5 bg-white h-11"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unset">— Not set —</SelectItem>
                    {MOBILITY_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Care needs <span className="text-charcoal/40 text-xs">(tap all that apply)</span></Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="care-needs-picker">
                {CARE_NEEDS.map((n) => {
                  const on = form.care_needs.includes(n);
                  return (
                    <button
                      type="button"
                      key={n}
                      data-testid={`need-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => toggleNeed(n)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-sage text-white border-sage" : "bg-white text-charcoal-soft border-charcoal/15 hover:border-sage/50"}`}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Authorized contact</Label>
                <Input data-testid="client-contact-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1.5 bg-white h-11" />
              </div>
              <div>
                <Label>Relationship</Label>
                <Input value={form.contact_relationship} onChange={(e) => setForm({ ...form, contact_relationship: e.target.value })} placeholder="Son, Daughter…" className="mt-1.5 bg-white h-11" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Contact phone</Label>
                <Input data-testid="client-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: formatPhone(e.target.value) })} className="mt-1.5 bg-white h-11" />
              </div>
              <div>
                <Label>Contact email <span className="text-charcoal/40 text-xs">(for alerts & invoices)</span></Label>
                <Input data-testid="client-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="mt-1.5 bg-white h-11" />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5 bg-white h-11" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 bg-white resize-none" rows={2} placeholder="Care preferences, routines, allergies (safety only)…" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} data-testid="save-client" className="w-full rounded-full bg-sage text-white py-3.5 font-medium hover:bg-sage-dark transition-colors">{editId ? "Save Changes" : "Add Client"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Family login dialog */}
      <Dialog open={famOpen} onOpenChange={setFamOpen}>
        <DialogContent className="bg-sand max-w-md" data-testid="family-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Create Family Login</DialogTitle></DialogHeader>
          <p className="text-sm text-charcoal-soft -mt-2">They'll be able to sign in and view this client's itemized services & billing.</p>
          <div className="space-y-4 mt-2">
            <div><Label>Family member name</Label><Input data-testid="family-name" value={famForm.name} onChange={(e) => setFamForm({ ...famForm, name: e.target.value })} className="mt-1.5 bg-white h-11" /></div>
            <div><Label>Email</Label><Input data-testid="family-email" type="email" value={famForm.email} onChange={(e) => setFamForm({ ...famForm, email: e.target.value })} className="mt-1.5 bg-white h-11" /></div>
            <div><Label>Temporary password</Label><Input data-testid="family-password" value={famForm.password} onChange={(e) => setFamForm({ ...famForm, password: e.target.value })} placeholder="6+ characters" className="mt-1.5 bg-white h-11" /></div>
          </div>
          <DialogFooter>
            <button onClick={saveFamily} data-testid="save-family" className="w-full rounded-full bg-sage text-white py-3 font-medium hover:bg-sage-dark transition-colors">Create Login</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
